import React from "react";
// Payment Processing Dashboard

// Custom
import Layout from "../../layout";
import CustomOutlinedSelect from "../../forms/select/OutlinedCustomSelect";
import CustomChip from "../chip/Chip";
import CustomSearch from "../search/Search";
import CustomDataTable from "../table/CustomTable";

// Mui
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid2";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";

// React icons
import { BiDollar } from "react-icons/bi";
import { FaCreditCard } from "react-icons/fa6";
import { FaRegClock } from "react-icons/fa";
import { LuTriangle } from "react-icons/lu";
import { FaCopy } from "react-icons/fa";
import { FaExternalLinkAlt } from "react-icons/fa";

// Svg
import Download from "../../assets/svg/download";
import { IconType } from "react-icons";
import { UnifiedIcon } from "../Icon/UnifiedIcon";

// Types
interface SummaryStatsData {
  title: string;
  amount: string;
  icon: IconType;
}

interface PaymentMethod {
  method: string;
  count: number;
  status: string;
  percentage: number;
  reconciled: number;
  pending: number;
  amount: number;
}

interface Transaction {
  id: string;
  customerName: string;
  customerID: string;
  billNumber: string;
  amount: number;
  method: string;
  date: string;
  time: string;
  status: string;
  receiptStatus: string;
  collectionRef: string;
}

interface SelectOption {
  value: string;
  label: string;
}

const COLUMNS = ["Transaction", "Bill Info", "Status", "Actions"];

const summaryStatsData: SummaryStatsData[] = [
  {
    title: "Daily Transactions",
    amount: "5680",
    icon: BiDollar,
  },
  {
    title: "Total Amount",
    amount: "880542.3",
    icon: FaCreditCard,
  },
  {
    title: "Pending Tasks",
    amount: "180",
    icon: FaRegClock,
  },
  {
    title: "Verification Issues",
    amount: "23",
    icon: LuTriangle,
  },
];

const paymentMethods: PaymentMethod[] = [
  {
    method: "Online/Card",
    count: 2456,
    status: "Success",
    percentage: 45,
    reconciled: 2398,
    pending: 58,
    amount: 367840.5,
  },
  {
    method: "Bank Transfer",
    count: 1890,
    status: "Success",
    percentage: 32,
    reconciled: 1845,
    pending: 45,
    amount: 298450.75,
  },
  {
    method: "Cash",
    count: 856,
    status: "Pending",
    percentage: 15,
    reconciled: 812,
    pending: 44,
    amount: 124680.25,
  },
  {
    method: "Check",
    count: 478,
    status: "Processing",
    percentage: 8,
    reconciled: 445,
    pending: 33,
    amount: 89570.8,
  },
];

const transactions: Transaction[] = [
  {
    id: "TXN-2024-0892",
    customerName: "John Smith",
    customerID: "CUST-78234",
    billNumber: "BILL-2024-1287",
    amount: 245.5,
    method: "Credit Card",
    date: "19 Sep 24",
    time: "06:55:55",
    status: "Success",
    receiptStatus: "Generated",
    collectionRef: "COL-2024-0892",
  },
  {
    id: "TXN-2024-0891",
    customerName: "Alice Johnson",
    customerID: "CUST-78235",
    billNumber: "BILL-2024-1286",
    amount: 382.75,
    method: "Bank Transfer",
    date: "19 Sep 24",
    time: "06:54:30",
    status: "Processing",
    receiptStatus: "Pending",
    collectionRef: "COL-2024-0891",
  },
];

const chipColor = (status: string): string => {
  if (status === "Success") return "#9CE976";
  if (status === "Processing") return "#edb64a";
  if (status === "Pending") return "#0396FF";
  return "#999B9D";
};

const PaymentProcessingDashboard: React.FC = () => {
  const rowsContent = transactions?.map((item: Transaction, index: number) => (
    <TableRow
      key={index}
      sx={{
        height: "50px",
        background: (index + 1) % 2 ? "#F9FCFE" : "transparent",
      }}
      className="custom_table_tbody">
      <TableCell sx={{ padding: 0 }}>
        <Typography className="custom_table_tbody_cell">
          {item.id} {item.customerName}
        </Typography>
      </TableCell>
      <TableCell sx={{ padding: 0 }}>
        <Typography className="custom_table_tbody_cell">
          {item.billNumber}${item.amount.toFixed(2)}
        </Typography>
      </TableCell>
      <TableCell sx={{ padding: 0 }}>
        <Typography className="custom_table_tbody_cell">
          <CustomChip
            label={item.status}
            backgroundColor={chipColor(item.status)}
          />
        </Typography>
      </TableCell>
      <TableCell sx={{ padding: 0 }}>
        <Stack direction={"row"} alignContent={"center"} gap={"15px"}>
          <UnifiedIcon icon={FaCopy} color="#999B9D" fontSize="16px" />
          <UnifiedIcon
            icon={FaExternalLinkAlt}
            color="#999B9D"
            fontSize="16px"
          />
        </Stack>
      </TableCell>
    </TableRow>
  ));

  const timePeriodOptions: SelectOption[] = [
    { value: "Today", label: "Today" },
    { value: "Week", label: "Week" },
    { value: "Month", label: "Month" },
  ];

  const summaryStatsCard = ({
    title,
    amount,
    icon,
  }: {
    title: string;
    amount: string;
    icon: IconType;
  }) => {
    return (
      <Card>
        <CardContent>
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            alignItems={"center"}>
            <Stack>
              <Typography variant="h5">{title}</Typography>
              <Typography variant="body1" mt={"10px"} fontWeight={700}>
                {amount}
              </Typography>
            </Stack>
            <UnifiedIcon icon={icon} color="#999B9D" fontSize="30px" />
          </Stack>
        </CardContent>
      </Card>
    );
  };

  return (
    <Layout>
      <Grid container spacing={2.25} mt={"20px"}>
        {summaryStatsData.map((item: SummaryStatsData, index: number) => {
          return (
            <Grid key={index} size={{ xs: 12, md: 3, lg: 3 }}>
              {summaryStatsCard(item)}
            </Grid>
          );
        })}
      </Grid>

      <Card sx={{ mt: "20px" }}>
        <CardHeader
          title={
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}>
              <Typography variant="h5">Payment Methods Distribution</Typography>

              <Box width={"25%"}>
                <CustomOutlinedSelect
                  placeholder={"Time Period"}
                  options={timePeriodOptions}
                />
              </Box>
            </Stack>
          }
        />
        <Divider />

        <CardContent>
          <Grid container spacing={2.25} mt={"20px"}>
            {paymentMethods.map((method: PaymentMethod, index: number) => (
              <Grid key={index} size={{ xs: 12, md: 4, lg: 4 }}>
                <Card sx={{ border: "1px solid #D7DAE7", padding: "16px" }}>
                  <Stack direction={"row"} justifyContent={"space-between"}>
                    <Stack direction={"column"} gap={"8px"}>
                      <Typography
                        variant="body1"
                        fontSize={"16px"}
                        fontWeight={600}>
                        {method.method}
                      </Typography>
                      <Typography variant="caption" fontSize={"14px"}>
                        {method.count.toLocaleString()} total transactions
                      </Typography>
                      <Typography variant="caption" fontSize={"14px"}>
                        ${method.amount.toLocaleString()}
                      </Typography>
                    </Stack>
                    <CustomChip
                      label={method.status}
                      backgroundColor={chipColor(method.status)}
                    />
                  </Stack>

                  <Box mt={"10px"}>
                    <Box
                      sx={{
                        width: `${method.percentage}%`,
                        height: "10px",
                        borderRadius: "8px",
                        background: chipColor(method.status),
                      }}
                    />

                    <Stack
                      direction={"row"}
                      justifyContent={"space-between"}
                      alignItems={"center"}
                      mt={"5px"}>
                      <Typography>
                        Share of volume:{" "}
                        <Typography component="span" fontWeight={600}>
                          {" "}
                          {method.percentage}%
                        </Typography>
                      </Typography>
                      <Typography>
                        Reconciled:{" "}
                        <Typography component="span" fontWeight={600}>
                          {method.reconciled}/{method.count}
                        </Typography>
                      </Typography>
                    </Stack>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mt: "20px", maxHeight: "650px" }}>
        <CardHeader
          title={
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}>
              <Typography variant="h5">Recent Transactions</Typography>

              <Stack direction={"row"} gap={"20px"} alignItems={"center"}>
                <CustomSearch
                  placeholder={"Enter Meter Id"}
                  sx={{ width: "400px" }}
                />
                <Download />
              </Stack>
            </Stack>
          }
        />
        <Divider />

        <CardContent>
          <CustomDataTable
            sx={{ maxHeight: "600px" }}
            columns={COLUMNS}
            rowsContent={rowsContent}
            pagination={false}
          />
        </CardContent>
      </Card>
    </Layout>
  );
};

export default PaymentProcessingDashboard;
